import { Button } from '@/shared/components/ui/button';
import { Clock, RefreshCw, Mail } from 'lucide-react';
import type { ClientOrInvitation } from '../types';
import { formatCooldownTime, formatRelativeDate, getInitials } from '../utils/clientDisplay';

type PendingInvitation = Extract<ClientOrInvitation, { type: 'PENDING' }>;

type PendingInvitationsListProps = {
  invitations: PendingInvitation[];
  cooldowns: Record<string, number>;
  resendingId: string | null;
  onResend: (inviteId: string) => void;
};

export function PendingInvitationsList({
  invitations,
  cooldowns,
  resendingId,
  onResend,
}: PendingInvitationsListProps) {
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-3">
        Pending Invitations ({invitations.length})
      </h4>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {invitations.map((invitation) => {
          const cooldownSeconds = cooldowns[invitation.id] || 0;
          const canResend = cooldownSeconds === 0 && resendingId !== invitation.id;
          const showCooldown = cooldownSeconds > 0;
          const isResending = resendingId === invitation.id;

          return (
            <div
              key={invitation.id}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-600 font-semibold text-sm">
                    {getInitials(invitation.name)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-medium text-gray-700 truncate">
                      {invitation.name}
                    </h4>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                      Pending
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {invitation.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  Invited {formatRelativeDate(invitation.invitedAt)}
                </div>
                {showCooldown ? (
                  <div className="flex items-center gap-1 text-xs text-amber-600">
                    <RefreshCw className="w-3 h-3" />
                    {formatCooldownTime(cooldownSeconds)}
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onResend(invitation.id)}
                    disabled={!canResend}
                    className="h-7 px-2 text-xs text-gray-600 hover:text-[#026857] hover:bg-[#026857]/5"
                  >
                    {isResending ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-3 h-3 mr-1" />
                        Resend
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
